import React, { FC, SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { isNil } from 'lodash-es';
import styled from 'styled-components';

import {
    Campaign,
    CampaignType,
    CampaignTypeFilter,
    CompressionFormat,
    ExportFileFormats,
} from '@dev/ee-api/ee-api-models';
import {
    CustomTableHeadersProps,
    EmptyState,
    MobileCardRowsProps,
    MobileTable,
    Paper,
    SimplePopoverPlainOption,
} from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { CampaignCreatorType, CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { ShowDialog } from '@/store/dialog/actions';
import { ApplicationState } from '@/store';
import { DropdownOptions } from '@/store/campaigns/types';
import {
    CopyCampaignsByNameStart,
    DeleteCampaigns,
    ExportCampaigns,
    PauseAutoresponder,
    PickCampaigns,
    ReactivateAutoresponder,
} from '@/store/campaigns/actions';
import useMobile from '@/services/hooks/useMobile';
import { DesktopListTable } from '@/components/Campaigns/components/DesktopListTable';
import ExportModal from '../../../../ExportModal';
import { useTableHeaders } from '../../hooks/useTableHeaders';
import { useTableRows } from '../../hooks/useTableRows';
import { getDropdownTableOptions } from '../../helperFunctions';

const MobileTableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
`;

interface MobileTableComponentInterface {
    headers: string[];
    rows: MobileCardRowsProps[];
    isEmpty: boolean;
    rowDropdownOptionsHandler: (
        ev: SyntheticEvent<HTMLElement>,
        option: { label: string; value: string },
        campaign: Campaign
    ) => void;
}

const MobileTableComponent: FC<MobileTableComponentInterface> = (props: MobileTableComponentInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { headers, isEmpty, rows, rowDropdownOptionsHandler } = props;

    if (isEmpty) {
        return (
            <Paper>
                <EmptyState text={t('emptystate')} />
            </Paper>
        );
    }

    const dropdownDataFilter = (
        options: SimplePopoverPlainOption[],
        returnOnItemClick: Campaign
    ): SimplePopoverPlainOption[] => getDropdownTableOptions(returnOnItemClick, true, t);

    return (
        <MobileTableContainer>
            <MobileTable
                headers={headers}
                rows={rows}
                dropdownDataFilter={dropdownDataFilter}
                buttonText={t('viewreports')}
                onClickButton={(_, singleCampaign): void => {
                    const uri = (
                        singleCampaign.Type === CampaignType.AbSplit
                            ? CampaignsPaths.CampaignAbResults
                            : CampaignsPaths.CampaignOverview
                    )
                        .replace(':type', singleCampaign.returnOnItemClick.Type)
                        .replace(':name', safelyEncodeBase64Url(singleCampaign.returnOnItemClick.Name));
                    dispatch(Push(uri));
                }}
                onItemClick={rowDropdownOptionsHandler}
            />
        </MobileTableContainer>
    );
};

interface TableComponentInterface {
    screenCampaignType: CampaignTypeFilter[];
}

const TableComponent: FC<TableComponentInterface> = (props: TableComponentInterface): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { screenCampaignType } = props;
    const isMobile = useMobile();
    const [exportOptions, setExportOptions] = useState({
        campaignNames: [],
        fileName: null,
        compressionFormat: CompressionFormat.Zip,
        fileFormat: ExportFileFormats.Csv,
    });
    const { campaigns, config } = useSelector((state: ApplicationState) => state);
    const fileNameAutorespondersRef = useRef(null);

    const openExportAutorespondersModal = (): void => {
        dispatch(
            ShowDialog({
                title: t('exportautoresponders'),
                body: (
                    <ExportModal
                        onCompressionChange={changeExportCompression}
                        onFileFormatChange={changeExportFileFormat}
                        inputRef={(element) => {
                            fileNameAutorespondersRef.current = element;
                        }}
                    />
                ),
                confirmLabel: t('export'),
                cancelLabel: t('cancel'),
                onClickConfirm: exportAutoresponders,
            })
        );
    };

    const changeExportCompression = (isEnabled: boolean): void => {
        setExportOptions({
            ...exportOptions,
            compressionFormat: isEnabled ? CompressionFormat.Zip : CompressionFormat.None,
        });
    };
    const changeExportFileFormat = (data: { value: ExportFileFormats; label: string }): void => {
        setExportOptions({ ...exportOptions, fileFormat: data.value });
    };
    const exportAutoresponders = (): void => {
        const fileName = fileNameAutorespondersRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams = {
            campaignNames: campaigns.checkedCampaigns,
            fileFormat,
            compressionFormat: exportOptions.compressionFormat,
            fileName,
        };
        dispatch(ExportCampaigns(exportParams));
    };
    const onSelectedCampaign = (event: SyntheticEvent<HTMLInputElement>, pickedCampaigns: string[]): void => {
        if (!isNil(event)) {
            event.stopPropagation();
            event.preventDefault();
        }
        dispatch(PickCampaigns(pickedCampaigns));
    };
    const editCampaign = (campaign: Campaign): void => {
        if (config.isStandalone) {
            dispatch(
                Push(
                    `${CampaignsPaths.CampaignCreatorName.replace(
                        pathsParams.type,
                        CampaignCreatorType.REGULAR
                    ).replace(pathsParams.name, safelyEncodeBase64Url(campaign.Name))}`
                )
            );
        } else {
            window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                ':name',
                safelyEncodeBase64Url(campaign.Name)
            )}`;
        }
    };
    const rowDropdownOptionsHandler = (
        ev: SyntheticEvent<HTMLElement>,
        option: { label: string; value: string },
        campaign: Campaign
    ): void => {
        switch (option.value) {
            case DropdownOptions.ViewDetails:
            case DropdownOptions.Edit:
                editCampaign(campaign);
                break;
            case DropdownOptions.CopyCampaign:
                dispatch(
                    ShowDialog({
                        title: t('copycampaign'),
                        body: `${t('copycampaignconfirmation')} "${campaign.Name}"?`,
                        confirmLabel: t('copy'),
                        cancelLabel: t('cancel'),
                        onClickConfirm: () => dispatch(CopyCampaignsByNameStart(campaign.Name)),
                    })
                );
                break;
            case DropdownOptions.Pause:
                dispatch(PauseAutoresponder(campaign));
                break;
            case DropdownOptions.Delete:
                dispatch(DeleteCampaigns([campaign.Name], screenCampaignType));
                break;
            case DropdownOptions.Reactivate:
                dispatch(ReactivateAutoresponder(campaign));
                break;
            default:
                break;
        }
    };
    const headers = useTableHeaders(openExportAutorespondersModal, screenCampaignType);
    const rows = useTableRows(rowDropdownOptionsHandler, editCampaign);
    if (isMobile) {
        return (
            <MobileTableComponent
                headers={headers as string[]}
                rows={rows}
                isEmpty={!campaigns.collection.length && !campaigns.loading}
                rowDropdownOptionsHandler={rowDropdownOptionsHandler}
            />
        );
    }
    return (
        <DesktopListTable headers={headers as CustomTableHeadersProps[]} rows={rows} onSelectRow={onSelectedCampaign} />
    );
};

export default TableComponent;
